import React from "react";
import { FileText, Share2 } from "lucide-react";
import { waLink, WHATSAPP_DISPLAY, EMAIL, DEFAULT_WA_MESSAGE } from "../lib/whatsapp";

const API = process.env.REACT_APP_BACKEND_URL;

const cardBase =
  "relative aspect-[85/35] w-full max-w-[340px] overflow-hidden rounded-xl border border-gold/60 bg-[#0a0a0a] shadow-2xl";

const CardFace = () => (
  <div data-testid="business-card-front" className={`${cardBase} grain flex flex-row items-stretch`}>
    <div className="pointer-events-none absolute inset-1.5 rounded-lg border border-gold/25" />
    {/* Zone marque (gauche) */}
    <div className="flex w-[32%] flex-col items-center justify-center px-2 py-2">
      <p className="font-display text-3xl leading-none tracking-tight text-gold">
        FK<span className="text-white">.</span>
      </p>
      <div className="my-1 h-px w-8 bg-gold" />
      <p className="text-center text-[6.5px] font-bold uppercase leading-tight tracking-[0.22em] text-[#e9cf8a]">
        Conseil &amp; Événement
      </p>
      <p className="mt-0.5 text-center font-display text-[8px] font-semibold italic leading-tight text-[#e9cf8a]">
        L'art du service d'exception
      </p>
      <p className="mt-0.5 text-center text-[7px] font-bold leading-tight text-gold">Digne d'un 5 étoiles</p>
    </div>
    {/* Séparateur vertical doré */}
    <div className="my-2.5 w-px self-stretch bg-gold/70" />
    {/* Zone contact (milieu) */}
    <div className="flex flex-1 flex-col justify-center px-2.5 py-2">
      <p className="text-[8px] font-bold leading-tight text-white">Serveurs · Maîtres d'hôtel · Hôtesses</p>
      <p className="mt-[2px] text-[7px] font-semibold leading-tight text-white">
        Mariages · Réceptions · Conférences
      </p>
      <p className="mt-1 text-[9px] font-bold text-[#e9cf8a]">WhatsApp {WHATSAPP_DISPLAY}</p>
      <p className="text-[7px] font-semibold leading-tight text-white">{EMAIL}</p>
      <p className="mt-[3px] text-[9.5px] font-bold tracking-wide text-gold">www.fk-evenement.fr</p>
      <p className="mt-0.5 text-[6.5px] font-semibold italic leading-tight text-white">Île-de-France · Europe</p>
    </div>
    {/* QR code vers le site (droite) */}
    <div className="flex items-center pr-2.5">
      <span className="block rounded-md border border-gold/70 bg-[#faf6ec] p-1">
        <img src="/images/qr-site.png" alt="QR code vers fk-evenement.fr" className="h-14 w-14" data-testid="card-qr" />
      </span>
    </div>
  </div>
);

const CropMarks = ({ children }) => (
  <div className="crop-zone relative inline-block">
    <span className="crop-mark crop-tl" />
    <span className="crop-mark crop-tr" />
    <span className="crop-mark crop-bl" />
    <span className="crop-mark crop-br" />
    <span className="crop-mark crop-tl2" />
    <span className="crop-mark crop-tr2" />
    <span className="crop-mark crop-bl2" />
    <span className="crop-mark crop-br2" />
    {children}
  </div>
);

export default function CarteVisite() {
  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "https://fk-evenement.fr/carte-de-visite";
    const shareData = {
      title: "FK Conseil & Événement — Carte de visite",
      text: "Carte de visite FK Conseil & Événement",
      url,
    };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (e) {
      // fall through to WhatsApp fallback
    }
    const wa = `https://wa.me/?text=${encodeURIComponent(`${shareData.text} — ${url}`)}`;
    if (typeof window !== "undefined") window.open(wa, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-[#141414] px-6 py-20" data-testid="business-card-page">
      <style>{`
        :root { --crop-pad: 14px; --crop-len: 10px; --crop-thk: 1px; }
        .crop-zone { padding: var(--crop-pad); }
        .crop-mark { position: absolute; background: #d4af37; pointer-events: none; }
        .crop-tl { top: 0; left: var(--crop-pad); width: var(--crop-len); height: var(--crop-thk); }
        .crop-tl2 { top: var(--crop-pad); left: 0; width: var(--crop-thk); height: var(--crop-len); }
        .crop-tr { top: 0; right: var(--crop-pad); width: var(--crop-len); height: var(--crop-thk); }
        .crop-tr2 { top: var(--crop-pad); right: 0; width: var(--crop-thk); height: var(--crop-len); }
        .crop-bl { bottom: 0; left: var(--crop-pad); width: var(--crop-len); height: var(--crop-thk); }
        .crop-bl2 { bottom: var(--crop-pad); left: 0; width: var(--crop-thk); height: var(--crop-len); }
        .crop-br { bottom: 0; right: var(--crop-pad); width: var(--crop-len); height: var(--crop-thk); }
        .crop-br2 { bottom: var(--crop-pad); right: 0; width: var(--crop-thk); height: var(--crop-len); }
        @media print {
          :root { --crop-pad: 10mm; --crop-len: 5mm; --crop-thk: 0.3mm; }
          body * { visibility: hidden; }
          #printable-cards, #printable-cards * { visibility: visible; }
          #printable-cards { position: absolute; inset: 0; background: white; padding: 10mm; }
          #printable-cards .card-print { width: 85mm; max-width: 85mm; margin-bottom: 8mm; box-shadow: none; }
          #printable-cards .crop-mark { background: #000 !important; }
        }
      `}</style>
      <div className="mx-auto max-w-3xl">
        <a href="/" data-testid="card-back-link" className="text-xs uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-gold">
          ← Retour au site
        </a>
        <h1 className="mt-8 font-display text-4xl tracking-tight text-white sm:text-5xl">
          Carte de visite <em className="italic text-gold">professionnelle</em>
        </h1>
        <p className="mt-4 text-sm text-white/50">
          Mini format de poche 85 × 35 mm, noir &amp; or — simple face. Téléchargez la planche A4 de 32 cartes avec
          traits de découpe, prête à imprimer et à distribuer à vos professionnels et prestataires.
        </p>
        <div id="printable-cards" className="mt-14">
          <CropMarks><div className="card-print"><CardFace /></div></CropMarks>
        </div>
        <div className="mt-14 flex flex-wrap gap-4">
          <a
            href={`${API}/api/cartes-visite-pdf`}
            data-testid="card-planche-a4-download"
            className="flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.03]"
          >
            <FileText className="h-4 w-4" />
            Planche A4 — 32 cartes (PDF)
          </a>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="card-print-button"
            className="rounded-full border border-gold/60 px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0a0a0a]"
          >
            Imprimer la carte
          </button>
          <button
            type="button"
            onClick={handleShare}
            data-testid="card-share-button"
            className="flex items-center gap-2 rounded-full border border-gold/60 px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0a0a0a]"
          >
            <Share2 className="h-4 w-4" />
            Partager la carte
          </button>
          <a
            href={waLink(DEFAULT_WA_MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="card-whatsapp-cta"
            className="rounded-full border border-white/20 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-white/80 transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            Nous contacter
          </a>
        </div>
      </div>
    </div>
  );
}
